import 'react-toastify/dist/ReactToastify.css';
import '../styles/merchant_buds.css';
import React, { useState, useEffect } from 'react';
import Chart from "react-apexcharts";
import BusinessService from '../services/business_services';
import { FaQrcode, FaPlus, FaDownload, FaShareAlt } from "react-icons/fa";
import { ToastContainer } from 'react-toastify';

import Header from './header';

let deferredPrompt = null;

const buildChart = (name, dataObj) => {
    const categories = Object.keys(dataObj);
    const data = categories.map(k => dataObj[k]);
    return {
        series: [{ name, data }],
        options: {
            chart: { height: 220, type: 'line', toolbar: { show: false } },
            stroke: { width: 4, curve: 'smooth' },
            xaxis: {
                type: 'datetime',
                categories,
                tickAmount: 5,
                labels: {
                    style: { colors: Array(categories.length).fill("#777") },
                    formatter: (value, timestamp, opts) =>
                        opts.dateFormatter(new Date(timestamp), 'dd MMM'),
                },
            },
            yaxis: { labels: { style: { colors: ["#777"] } } },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'dark',
                    gradientToColors: ['#FDD835'],
                    shadeIntensity: 1,
                    type: 'horizontal',
                    opacityFrom: 1,
                    opacityTo: 1,
                },
            },
        },
    };
};

function MerchantBuds() {
    const [buds, setBuds] = useState([]);
    const [selectedUuid, setSelectedUuid] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [apexOne, setApexOne] = useState(null);
    const [apexTwo, setApexTwo] = useState(null);
    const [cumulatedUsers, setCumulatedUsers] = useState(0);
    const [impressions, setImpressions] = useState(0);
    const [showInstall, setShowInstall] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        const ua = window.navigator.userAgent;
        const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
        const dismissed = sessionStorage.getItem('pwa-install-dismissed');
        const isMobile = window.innerWidth <= 800;

        if (iOS && !isStandalone && !dismissed && isMobile) {
            setIsIOS(true);
            setShowInstall(true);
        }

        const handler = (e) => {
            e.preventDefault();
            deferredPrompt = e;
            if (!dismissed && isMobile) setShowInstall(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setShowInstall(false);
        deferredPrompt = null;
    };

    const dismissInstall = () => {
        setShowInstall(false);
        sessionStorage.setItem('pwa-install-dismissed', '1');
    };

    const daysAgo = n => {
        const d = new Date();
        d.setDate(d.getDate() - Math.abs(n));
        return d;
    };

    const loadStats = async (bud_uuid) => {
        const since = daysAgo(10).toISOString().split('T')[0];
        const stats = await BusinessService.businessUserStats(bud_uuid, since);
        setCumulatedUsers(stats.nb_holders || 0);
        setApexOne(buildChart('Users', stats.new_players.cumulated_daily));

        const views = await BusinessService.businessAnalytics(since);
        setImpressions(Object.values(views).reduce((a, b) => a + b, 0));
        setApexTwo(buildChart('Impressions', views));
    };

    const loadBuds = async () => {
        const data = await BusinessService.budsBusiness();
        setBuds(data);
        setLoaded(true);
        if (data.length > 0) {
            setSelectedUuid(data[0].bud_uuid);
            loadStats(data[0].bud_uuid);
        }
    };

    useEffect(() => {
        loadBuds();
        // eslint-disable-next-line
    }, []);

    const onSelectChange = (e) => {
        const uuid = e.target.value;
        setSelectedUuid(uuid);
        if (uuid) {
            setApexOne(null);
            setApexTwo(null);
            loadStats(uuid);
        }
    };

    const selectedBud = buds.find(b => b.bud_uuid === selectedUuid);

    return (
        <div className="container">
            <Header page={"Buds"} />

            <div className="content simple" style={{ paddingTop: 40 }}>
                <div className="mb-page">
                    <div className="mb-intro">
                        <h1>Gérez vos Buds</h1>
                        <p>Sélectionnez un de vos Buds pour voir ses statistiques et gérer sa distribution.</p>
                    </div>

                    {loaded && buds.length === 0 && (
                        <div className="mb-empty">
                            <p>Vous n'avez encore aucun Bud actif.</p>
                            <div className="mb-btn primary" style={{ maxWidth: 280, margin: "0 auto" }}
                                onClick={() => window.location.href = "/merchants/buds/new"}>
                                <FaPlus /> Obtenir un nouveau Bud
                            </div>
                        </div>
                    )}

                    {buds.length > 0 && (
                        <>
                            <div className="mb-selector">
                                <select id="bud-select" value={selectedUuid} onChange={onSelectChange}>
                                    {buds.map(bud => (
                                        <option key={bud.bud_uuid} value={bud.bud_uuid}>
                                            {bud.bud_info.bud_name} — {bud.bud_info.bud_level.toUpperCase()}
                                        </option>
                                    ))}
                                </select>
                                {/* <div className="mb-new-btn" onClick={() => window.location.href = "/merchants/buds/new"}>
                                    <FaPlus /> Nouveau
                                </div> */}
                            </div>

                            {selectedBud && (
                                <>
                                    <div className="mb-bud-header">
                                        <div className="mb-bud-thumb"
                                            style={{ backgroundImage: `url('${selectedBud.bud_info.img_url}')` }} />
                                        <div className="mb-bud-meta">
                                            <h2>{selectedBud.bud_info.bud_name}</h2>
                                            <span className={`mb-rarity-badge ${selectedBud.bud_info.bud_level}`}>
                                                {selectedBud.bud_info.bud_level.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mb-kpis">
                                        <div className="mb-kpi">
                                            <p className="mb-kpi-value">{cumulatedUsers}</p>
                                            <p className="mb-kpi-label">Clients uniques</p>
                                        </div>
                                        <div className="mb-kpi">
                                            <p className="mb-kpi-value">{impressions}</p>
                                            <p className="mb-kpi-label">
                                                Impressions profil
                                                <small>(10 derniers jours)</small>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mb-charts">
                                        <div className="mb-chart">
                                            {apexOne && <Chart options={apexOne.options} series={apexOne.series} type="line" height={220} />}
                                        </div>
                                        <div className="mb-chart">
                                            {apexTwo && <Chart options={apexTwo.options} series={apexTwo.series} type="line" height={220} />}
                                        </div>
                                    </div>

                                    <div className="mb-actions">
                                        <div className="mb-btn primary"
                                            onClick={() => window.location.href = `/merchants/distribution/${selectedBud.bud_uuid}`}>
                                            <FaQrcode /> Gérer les distributions
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            <ToastContainer />

            {showInstall && <div className="pwa-install-overlay">
                <div className="pwa-install-popup">
                    <h3>Installer LoyalBuds</h3>
                    {isIOS ? (
                        <>
                            <p>Pour installer l'app sur votre iPhone :</p>
                            <div className="pwa-ios-steps">
                                <div className="pwa-ios-step">
                                    <span className="pwa-ios-step-num">1</span>
                                    <span>Appuyez sur <FaShareAlt style={{ verticalAlign: "middle", color: "#007AFF" }} /> en bas de Safari</span>
                                </div>
                                <div className="pwa-ios-step">
                                    <span className="pwa-ios-step-num">2</span>
                                    <span>Faites défiler et appuyez sur <strong>« Sur l'écran d'accueil »</strong></span>
                                </div>
                                <div className="pwa-ios-step">
                                    <span className="pwa-ios-step-num">3</span>
                                    <span>Confirmez en appuyant sur <strong>« Ajouter »</strong></span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <p>Accédez rapidement à votre espace depuis votre écran d'accueil.</p>
                            <div className="pwa-install-actions">
                                <div className="pwa-install-btn" onClick={handleInstall}>
                                    <FaDownload /> Installer
                                </div>
                            </div>
                        </>
                    )}
                    <div className="pwa-install-dismiss" onClick={dismissInstall}>
                        Plus tard
                    </div>
                </div>
            </div>}
        </div>
    );
}

export default MerchantBuds;
